<script>
import {
  defineComponent,
  computed,
  ref,
  reactive,
  watchEffect,
  watch,
  toRefs,
} from "@vue/composition-api";
import moment from "moment";
import {
  CheckProjectServiceProxy,
  CheckItemServiceProxy,
} from "@/service-proxies/app-v2/tpm.service-proxies";

import useModal from "@/hooks/useModal";
import useTable from "@/hooks/useTable";
import { cloneDeep } from "lodash";
const cpsp = new CheckProjectServiceProxy();
const cisp = new CheckItemServiceProxy();

const cloneWithout = (origin, list) => {
  const o = Object.create(null);
  Object.keys(origin).forEach((key) => {
    if (!list.includes(key)) o[key] = origin[key];
  });
  return o;
};

export default defineComponent({
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    initialData: {
      type: Object,
      default: () => ({}),
    },
    isUpdate: {
      type: Boolean,
      default: false,
    },
    checkList: {
      type: Array,
      default: () => [],
    },
  },
  setup(props, { root, emit }) {
    const { modalAttrs, modalEvents } = useModal(
      {
        title: root.$t("page_tpm_check_project_create") || "新建点检",
        visible: true,
      },
      {
        cancel() {
          emit("close");
        },
      }
    );
    const showDisabled = ref(true);

    const form = reactive({ codeRuleId: "", code: "", name: "", remark: "" });
    const { checkList } = props;
    const rules = computed(() => ({
      code: [
        {
          required: showDisabled.value,
          message: root.$t("page_dform_must") || "请输入",
          trigger: "change",
        },
        { max: 32 },
      ],
      name: [
        {
          required: true,
          message: root.$t("page_dform_must") || "请输入",
          trigger: "change",
        },
        { max: 64 },
      ],
      remark: [{ max: 100 }], 
    }));

    const { tableAttrs } = useTable({
      columns: [
        {
          key: "action",
          title: "编辑",
          scopedSlots: { customRender: "action" },
          i18n: "actions",
        },
        {
          key: "name",
          title: "点检项(必填)",
          scopedSlots: { customRender: "name" },
          i18n: "page_tpm_check_item_name",
        },
        {
          key: "valueType",
          title: "数值类型",
          scopedSlots: { customRender: "valueType" },
          i18n: "page_tpm_check_item_type",
        },
        {
          key: "requirement",
          title: "点检要求(必填)",
          scopedSlots: { customRender: "requirement" },
          i18n: "page_tpm_check_item_require",
        },
        {
          key: "standardValue",
          title: "标准数值(必填)",
          scopedSlots: { customRender: "standardValue" },
          i18n: "page_tpm_check_item_standard",
        },
      ],
      rowSelection: null,
      pagination: false,
    });

    const close = () => {
      emit("close");
    };

    const TYPE_MAP = {
      TEXT: "TEXT",
      NUMBER: "NUMBER",
      DATE: "DATE",
    };

    const addItem = () => {
      const newItem = {
        name: null,
        valueType: TYPE_MAP.TEXT,
        requirement: null,
        standardValue: null,
        _isAdd: true,
      };

      tableAttrs.dataSource.push(newItem);
    };
    const { isUpdate } = toRefs(props);

    const removeIds = ref([]);
    const removeItem = (record) => {
      if (isUpdate.value) removeIds.value.push(record.id);
      const index = tableAttrs.dataSource.findIndex((item) => item === record);
      tableAttrs.dataSource.splice(index, 1);
    };

    const saveItem = (record) => {
      if (!record.name || !record.requirement || !record.standardValue) {
        root.$message.warning(
          root.$t("page_tpm_check_three") || "请输入点检项, 点检要求, 标准数值"
        );
        return;
      }
      if (record.valueType === TYPE_MAP.DATE && !!record.standardValue) {
        record.standardValue = record.standardValue.format("YYYY-MM-DD");
      }
      record._isAdd = false;
    };

    const onTypeChange = (value, record) => {
      record.standardValue = null;
      record.valueType = value;
    };

    const reset = () => {
      if (isUpdate.value) {
        form.code = props.initialData.code;
        form.name = props.initialData.name;
        form.remark = props.initialData.remark;
        form.id = props.initialData.id;
        tableAttrs.dataSource = cloneDeep(props.initialData.list) || [];
        removeIds.value = [];
      } else {
        form.code = null;
        form.name = null;
        form.remark = null;
        tableAttrs.dataSource = [];
        removeIds.value = [];
      }
    };
    const handlechange = (val) => {
      if (val) {
        showDisabled.value = false;
        form.code = ''
      } else {
        showDisabled.value = true;
    }
    };

    const submit = async () => {
      if (!form.name) {
        root.$message.error(
          root.$t("page_tpm_check_two") || "请输入点检项目"
        );
        return;
      }
      if (
        tableAttrs.dataSource.filter((item) => !item.name).length > 0 ||
        tableAttrs.dataSource.filter((item) => !item.requirement).length > 0 ||
        tableAttrs.dataSource.filter((item) => !item.standardValue).length > 0
      ) {
        root.$message.error(root.$t("page_tpm_check_one") || "请输入点检项");
        return;
      }
      if (tableAttrs.dataSource.filter((item) => item._isAdd).length > 0) {
        root.$message.error(
          root.$t("page_tpm_check_project_save" || "请保存点检项")
        );
        return;
      }
      if (tableAttrs.dataSource.length === 0) {
        root.$message.error(root.$t("page_tpm_check_one") || "请输入点检项");
        return;
      }
      const body = {
        ...form,
        list: tableAttrs.dataSource
          .filter((item) => !item._isAdd)
          .map((item) => cloneWithout(item, ["_isAdd"])),
      };

      modalAttrs.confirmLoading = true;
      try {
        if (isUpdate.value) {
          for (let id of removeIds.value) {
            if (id) await cisp.deleteCheckItemById(id);
          }
          await cpsp.updateCheckProjectAndItems(body);
        } else {
          await cpsp.createCheckProject(body);
        }
        modalAttrs.confirmLoading = false;
        close();
      } catch (e) {
        console.error(e);
        modalAttrs.confirmLoading = false;
      }
    };

    watch(
      isUpdate,
      (bool) => {
        if (bool) {
          modalAttrs.title =
            root.$t("page_tpm_check_project_edit") || "编辑点检";
          form.code = props.initialData.code;
          form.name = props.initialData.name;
          form.remark = props.initialData.remark;
          form.id = props.initialData.id;
          tableAttrs.dataSource = cloneDeep(props.initialData.list) || [];
        } else {
          form.code = null;
          form.name = null;
          form.remark = null;
          form.id = null;
          tableAttrs.dataSource = [];
        }
      },
      { immediate: true },
    );

    return {
      modalAttrs,
      modalEvents,

      form,
      rules,

      tableAttrs,

      submit,
      close,
      reset,
      addItem,
      removeItem,
      saveItem,
      TYPE_MAP,
      onTypeChange,
      handlechange,
      showDisabled,
    };
  },
});
</script>

<template>
  <a-modal v-bind="modalAttrs" v-on="modalEvents">
    <a-form-model
      :model="form"
      :rules="rules"
      :label-col="{ span: 5 }"
      :wrapper-col="{ span: 12 }"
    >
      <a-form-model-item
        :style="isUpdate ? { display: 'none' } : { display: 'block' }"
        :label="$t('page_system_coding_rules') || '编码规则'"
      >
        <a-select :placeholder="$t('please_select') + $t('page_system_coding_rules') || '编码规则'" v-model="form.codeRuleId" @change="handlechange" allowClear>
          <template v-for="item in checkList">
            <a-select-option :value="item.id" :key="item.id">
              {{ item.name }}
            </a-select-option>
          </template>
        </a-select>
      </a-form-model-item>
      <a-form-model-item
        :label="$t('page_tpm_check_project_code') || '点检编码'"
        prop="code"
      >
        <a-input :placeholder="$t('common_please_input') + $t('page_tpm_check_project_code') || '点检编码'" v-model="form.code" :disabled="isUpdate || !showDisabled" :maxLength="32" />
      </a-form-model-item>
      <a-form-model-item
        :label="$t('page_tpm_check_project_name') || '点检项目'"
        prop="name"
      >
        <a-input :placeholder="$t('common_please_input') + $t('page_tpm_check_project_name') || '点检项目'" v-model="form.name" :maxLength="64" />
      </a-form-model-item>
      <a-form-model-item
        :label="$t('page_tpm_check_project_remark') || '备注'"
        prop="remark"
      >
        <a-textarea :placeholder="$t('common_please_input') + $t('page_tpm_check_project_remark') || '备注'" v-model="form.remark" :maxLength="100" />
      </a-form-model-item>
    </a-form-model>

    <ml-table v-bind="tableAttrs">
      <template #action="record">
        <template v-if="record._isAdd">
          <a @click="saveItem(record)">{{ $t("save") || "保存" }}</a>
          <a-divider type="vertical" />
          <a @click="removeItem(record)">{{ $t("cancel") || "取消" }}</a>
        </template>
        <template v-else>
          <a-popconfirm
            :title="$t('page_resource_pool_confirm_delete')"
            :ok-text="$t('yes')"
            :cancel-text="$t('no')"
            @confirm="removeItem(record)"
          >
            <a style="color: var(--color-red)">{{ $t("delete") || "删除" }}</a>
          </a-popconfirm>
        </template>
      </template>
      <template #name="record">
        <template v-if="record._isAdd">
          <a-input v-model="record.name" :maxLength="32" />
        </template>
        <template v-else>{{ record.name }}</template>
      </template>
      <template #valueType="_, record">
        <template v-if="record._isAdd">
          <a-select
            :value="record.valueType"
            style="width: 100%"
            @change="onTypeChange($event, record)"
          >
            <a-select-option
              v-for="(value, name) in TYPE_MAP"
              :key="name"
              :value="value"
              >{{ name.toUpperCase() }}</a-select-option
            >
          </a-select>
        </template>
        <template v-else>{{ record.valueType }}</template>
      </template>
      <template #requirement="record">
        <template v-if="record._isAdd">
          <a-input v-model="record.requirement" :maxLength="48" />
        </template>
        <template v-else>{{ record.requirement }}</template>
      </template>
      <template #standardValue="record">
        <template v-if="record._isAdd">
          <template v-if="record.valueType === TYPE_MAP.TEXT">
            <a-input v-model="record.standardValue" :maxLength="48" />
          </template>
          <template v-if="record.valueType === TYPE_MAP.NUMBER">
            <a-input-number
              v-model="record.standardValue"
              style="width: 100%"
            />
          </template>
          <template v-if="record.valueType === TYPE_MAP.DATE">
            <a-date-picker v-model="record.standardValue" />
          </template>
        </template>
        <template v-else>{{ record.standardValue }}</template>
      </template>
    </ml-table>
    <a-button class="mt-2" block type="primary" @click="addItem">{{
      $t("page_tpm_check_item_create") || "添加点检项"
    }}</a-button>

    <template #footer>
      <a-button @click="close">{{ $t("cancel") || "取消" }}</a-button>
      <a-button type="danger" @click="reset">{{
        $t("page_bill_settings_reset") || "重置"
      }}</a-button>
      <a-button
        type="primary"
        :loading="modalAttrs.confirmLoading"
        @click="submit"
        >{{ $t("submit") || "提交" }}</a-button
      >
    </template>
  </a-modal>
</template>
